import { useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { IconOptionsReport, IconOptionsThrash } from 'assets/svg/sprite'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Report from 'components/UI/Modal/Report/Report'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useModalContext } from 'context/modalContext'
import { useOutsideAlerter, useReportPost } from 'helpers/hooks'
// import { getPlaylists } from 'services/endpoints/media'
import { useGlobalStore } from 'store/global'
import screens from 'styles/screens'
import { type IOption } from 'types/iTypes'

interface PostOptionsDropdownProps {
  role: string
  content: string
  isOptionsNavOpen: boolean
  postId: string
  setIsOptionsNavOpen: (_value: boolean) => void
  options?: IOption[] | null
  deleteFn?: () => void
  withoutBottomNavigation?: boolean
}

export const PostOptionsDropdown = ({
  role,
  isOptionsNavOpen,
  postId,
  setIsOptionsNavOpen,
  options,
  deleteFn,
  content,
  withoutBottomNavigation = false
}: PostOptionsDropdownProps) => {
  const modalData = useModalContext()
  const optionsRef = useRef<HTMLDivElement>(null)
  // const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { accountSetupPopup, setAccountSetupPopup } = useGlobalStore()
  const { hideFooter } = useHeaderLayout()
  const { reportPostMutation } = useReportPost()

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const [shouldRender, setShouldRender] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useOutsideAlerter(optionsRef, () => {
    setIsOptionsNavOpen(false)
  })

  useEffect(() => {
    setPortalTarget(document.body)
  }, [])

  useEffect(() => {
    if (isOptionsNavOpen) {
      setAccountSetupPopup({ visible: false })
    } else if (accountSetupPopup.shouldShow) {
      setAccountSetupPopup({ visible: true })
    }
  }, [isOptionsNavOpen, accountSetupPopup.shouldShow, setAccountSetupPopup])

/*   useEffect(() => {
    if (isOptionsNavOpen) {
      queryClient.prefetchQuery({
        queryKey: ['getPlaylists'],
        queryFn: async () => await getPlaylists()
      })
    }
  }, [isOptionsNavOpen, queryClient]) */

useEffect(() => {
  if (isOptionsNavOpen) {
    setShouldRender(true)

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    })

    return () => cancelAnimationFrame(raf)
  }

  setIsVisible(false)
  setShouldRender(false)
}, [isOptionsNavOpen])

  const itemTitle = () => {
    switch (content) {
      case 'post':
        return t('deletePost')
      case 'premium':
        return t('Delete Post')
      case 'story':
        return t('deleteStory')
      case 'message':
        return t('deleteMessage')
      case 'stream':
        return t('Delete Stream')
      default:
        return t('delete')
    }
  }

const handlePostDelete = () => {
  // Close the post options dropdown immediately
  setIsOptionsNavOpen(false)

  // Open the delete confirmation modal
  modalData.addModal(
    t('deletePost'),
    <ConfirmModal
      body={t('areYouSureYouWantToDeleteThisPost')}
      confirmFn={() => {
        deleteFn?.()
      }}
    />
  )
}

  const handlePostReport = () => {
    modalData.addModal(
      t('reportPost'),
      <Report
        type='content'
        confirmFn={(message: string, reportType: number) => {
          reportPostMutation.mutate(
            { postId, message, reportType },
            {
              onSuccess: () => {
                setIsOptionsNavOpen(false)
                modalData.clearModal()
              }
            }
          )
        }}
        closeFn={() => modalData.clearModal()}
      />
    )
  }

  if (!portalTarget || !shouldRender) return null

return createPortal(
  <>
    <BackgroundContainer
      className={isVisible ? 'active' : ''}
      onClick={() => setIsOptionsNavOpen(false)}
    />

      <OptionsDropdownWrapper
        ref={optionsRef}
        className={isVisible ? 'active' : ''}
        $withoutBottomNavigation={withoutBottomNavigation}
        $hideFooter={hideFooter}
      >
          <div className='post__options__dropdown__section'>
            {options?.map((opt: IOption, key: number) => (
              <div
                key={key}
                className={`post__options__option ${opt.disabled ? 'post__options__option--disabled' : ''}`}
                onClick={() => opt.action(postId)}
              >
                {typeof opt.icon === 'string' ? (
                  <img src={opt.icon} alt={opt.text} />
                ) : (
                  opt.icon
                )}
                <span>{opt.text}</span>
              </div>
            ))}
          </div>

          <div className='separator__line' />

          <div className='post__options__dropdown__section--bot'>
            {role === 'owner' ? (
              <div className='post__options__option' onClick={handlePostDelete}>
                <IconOptionsThrash />
                <span>{itemTitle()}</span>
              </div>
            ) : (
              <div className='post__options__option' onClick={handlePostReport}>
                <IconOptionsReport />
                <span>{t('reportPost')}</span>
              </div>
            )}
          </div>
        </OptionsDropdownWrapper>
  </>,
  portalTarget
)
}


const BackgroundContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999998;

  background: transparent;

  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;

  &.active {
    opacity: 1;
    pointer-events: auto;
  }
`

interface OptionsDropdownWrapperProps {
  $withoutBottomNavigation: boolean
  $hideFooter: boolean
}

const OptionsDropdownWrapper = styled.div<OptionsDropdownWrapperProps>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;

  z-index: 999999;

  background: #fff;
  box-shadow: 0 2rem 6rem rgba(17, 29, 41, 0.35);

  width: 100%;
  height: auto;
  min-height: 0;
  max-height: calc(100dvh - 3.2rem);

  overflow-y: auto;

  padding: 3rem 0;
  border-radius: 2.5rem 2.5rem 0 0;

  transform: translate3d(0, 100%, 0);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transition:
    transform 0.1s ease-in-out,
    visibility 0.1s ease-in-out;
  .separator__line {
    display: block;
    height: 1px;
    background-color: #d9d9d9;
    margin: 0 3rem;
  }
  &.active {
    transform: translate3d(0, 0, 0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  @media (min-width: ${screens.medium}) {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;

    width: calc(100% - 3.2rem);
    max-width: 500px;

    padding: 3rem 0;
    border-radius: 2.5rem;

    transform: translate3d(-50%, -50%, 0) scale(0.9);

    &.active {
      transform: translate3d(-50%, -50%, 0) scale(1);
    }
  }

  @media (max-width: ${screens.medium}) {
    padding-bottom: ${({ $withoutBottomNavigation, $hideFooter }) =>
      $withoutBottomNavigation
        ? $hideFooter
          ? '1.6rem'
          : '8rem'
        : '3rem'};
  }
`
